import { useState, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import starIcon from '../../../assets/dashboard_assets/star.png';
import AuthContext from '../../../context/AuthContext';
import { createRazorpayOrder, verifyRazorpayPayment, loadRazorpayScript } from '../../../services/paymentApi';

const MIN_LISTINGS = 3;
const MAX_LISTINGS = 300;
const MRP = 400;
const CREDITS_PER_LISTING = 50;
const GST_RATE = 0.18;

const getPerListing = (count) => {
    if (count <= 10) return 260;
    if (count <= 50) return 250;
    if (count <= 100) return 235;
    if (count <= 150) return 220;
    if (count <= 200) return 210;
    return 200;
};


const SummaryRow = ({ label, value, valueClassName }) => (
    <div className="w-full flex justify-between items-center">
        <span className="text-[#737373] text-sm font-medium leading-6 font-sans">{label}</span>
        <span className={valueClassName || 'text-[#737373] text-sm font-medium leading-6 font-sans'}>{value}</span>
    </div>
);

const CreditSlider = ({ presetListings, onPresetConsumed }) => {
    const [searchParams] = useSearchParams();
    const authCtx = useContext(AuthContext);
    const initialCredits = Math.min(MAX_LISTINGS, Math.max(MIN_LISTINGS, Number(searchParams.get('credits')) || 8));
    const [listings, setListings] = useState(initialCredits);
    const [inputText, setInputText] = useState(String(initialCredits));

    // When Buy Pack sets presetListings, update slider
    useEffect(() => {
        if (presetListings && presetListings >= MIN_LISTINGS && presetListings <= MAX_LISTINGS) {
            updateListings(presetListings);
            if (onPresetConsumed) onPresetConsumed();
        }
    }, [presetListings]);
    const [inputShake, setInputShake] = useState(false);
    const [paying, setPaying] = useState(false);
    const [billingForm, setBillingForm] = useState(null); // null = not loaded, {} = no address
    const [useDefault, setUseDefault] = useState(true);
    const [newAddr, setNewAddr] = useState({ company_name: '', gstin: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India' });
    const [setAsDefault, setSetAsDefault] = useState(true);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    // Load billing address from profile
    useState(() => {
        if (!authCtx.token) return;
        fetch(`${API_URL}/auth/billing-address`, { headers: { 'Authorization': `Bearer ${authCtx.token}` } })
            .then(r => r.json())
            .then(data => {
                if (data.success && data.billing_address?.address_line1) {
                    setBillingForm(data.billing_address);
                } else {
                    setBillingForm({});
                    setUseDefault(false);
                }
            })
            .catch(() => { setBillingForm({}); setUseDefault(false); });
    }, [authCtx.token]);

    const activeBilling = useDefault && billingForm?.address_line1 ? billingForm : newAddr;
    const hasBillingAddr = activeBilling.address_line1 && activeBilling.city && activeBilling.state && activeBilling.pincode;

    // Auto-save new address to profile
    const saveNewAddrToProfile = async (addr) => {
        try {
            await fetch(`${API_URL}/auth/billing-address`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${authCtx.token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(addr),
            });
        } catch {}
    };

    const handlePayment = async () => {
        if (!authCtx.token || paying) return;
        if (!hasBillingAddr) { alert('Please fill billing address'); return; }
        // If new address and user wants to save as default
        if (!useDefault && newAddr.address_line1 && setAsDefault) {
            saveNewAddrToProfile(newAddr);
            setBillingForm(newAddr); // update local cache
        }
        setPaying(true);
        try {
            const credits = listings * CREDITS_PER_LISTING;
            const subtotal = listings * getPerListing(listings);
            const gst = Math.round(subtotal * GST_RATE);
            const amount = subtotal + gst;
            const mrp = listings * MRP;
            const discount = mrp - subtotal;
            const discountPctVal = Math.round(((MRP - getPerListing(listings)) / MRP) * 100);
            const orderRes = await createRazorpayOrder(authCtx.token, amount, 'token_pack', {
                credits, subtotal, gst, mrp, discount, discount_pct: discountPctVal,
                customer_gstin: activeBilling.gstin || undefined,
                billing_address: hasBillingAddr ? activeBilling : undefined,
                customer_email: (authCtx.user || {}).email || '',
                customer_phone: (authCtx.user || {}).phonenumber || '',
            });
            if (!orderRes.success) { alert(orderRes.message || 'Order failed'); return; }

            const isLoaded = await loadRazorpayScript();
            if (!isLoaded) { alert('Payment gateway failed to load'); return; }

            const user = authCtx.user || {};
            const rzp = new window.Razorpay({
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: orderRes.order.amount,
                currency: 'INR',
                order_id: orderRes.order.id,
                name: 'EcomBuddha',
                description: `${credits} Credits - Pay As You Go`,
                image: '/favicon.png',
                notify: { email: false, sms: false },
                prefill: {
                    name: user.fullname || user.username || '',
                    email: user.email || '',
                    contact: user.phonenumber || '',
                },
                handler: async (response) => {
                    try {
                        const res = await verifyRazorpayPayment(authCtx.token, response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature);
                        if (res.success && authCtx.updateUserCredit && res.user_balance) {
                            authCtx.updateUserCredit(res.user_balance.credits);
                            // Reset form
                            updateListings(8);
                            setUseDefault(true);
                            setNewAddr({ company_name: '', gstin: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India' });
                        } else { alert('Payment verification failed.'); }
                    } catch { alert('Payment verification error.'); }
                },
                theme: { color: '#2766AA', backdrop_color: 'rgba(39, 102, 170, 0.15)' },
                modal: { ondismiss: () => {} },
            });
            rzp.open();
        } catch (err) { alert(err.message || 'Something went wrong'); }
        finally { setPaying(false); }
    };

    const perListing = getPerListing(listings);
    const credits = listings * CREDITS_PER_LISTING;
    const subtotal = listings * perListing;
    const mrpTotal = listings * MRP;
    const savings = mrpTotal - subtotal;
    const discountPct = Math.round(((MRP - perListing) / MRP) * 100);
    const gst = Math.round(subtotal * GST_RATE);
    const total = subtotal + gst;
    const perCredit = (subtotal / credits).toFixed(2);
    const sliderProgress = ((listings - MIN_LISTINGS) / (MAX_LISTINGS - MIN_LISTINGS)) * 100;
    const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

    const updateListings = (v) => {
        setListings(v);
        setInputText(String(v));
    };

    return (
        <div className="w-full">
            <div className="flex items-stretch gap-4">
                {/* Slider Card */}
                <div className="flex-1 bg-[#FEFEFF] shadow-[4px_46px_134px_14px_rgba(96,146,200,0.10)] rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#D0E7FF] px-5 py-4 flex flex-col justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <div className="text-[#505050] text-lg font-semibold font-heading">Customise your credit pack</div>
                        <span className="text-[#898989] text-sm font-medium">Choose the exact number of listings you need.</span>
                    </div>

                    {/* Listings input + credits display */}
                    <div className="flex items-center justify-between gap-2.5">
                        <div className="flex items-center gap-2.5">
                            <div className="text-[#898989] text-xs font-semibold uppercase tracking-wide">Listings :</div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => updateListings(Math.max(MIN_LISTINGS, listings - 1))}
                                    disabled={listings <= MIN_LISTINGS}
                                    className="w-7 h-7 flex items-center justify-center text-[#2766AA] bg-[#EEF5FF] rounded-md hover:bg-[#D9EAFF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-lg font-semibold leading-none"
                                    aria-label="Decrease listings"
                                >
                                    −
                                </button>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={inputText}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/\D/g, '');
                                        setInputText(raw);
                                        if (raw === '') return;
                                        const num = Number(raw);
                                        if (num >= MIN_LISTINGS) {
                                            setListings(Math.min(MAX_LISTINGS, num));
                                        }
                                    }}
                                    onBlur={() => {
                                        const num = Number(inputText);
                                        if (!inputText || num < MIN_LISTINGS) {
                                            setInputShake(true);
                                            setTimeout(() => setInputShake(false), 500);
                                            updateListings(MIN_LISTINGS);
                                        } else {
                                            updateListings(Math.min(MAX_LISTINGS, num));
                                        }
                                    }}
                                    className={`w-[48px] text-[#2766AA] text-base font-semibold bg-[#EEF5FF] outline-none text-center rounded-md py-1 focus:ring-1 focus:ring-[#78B3F3] transition-all ${inputShake ? 'ring-1 ring-red-400 bg-red-50' : ''}`}
                                    style={inputShake ? { animation: 'headShake 0.5s ease' } : {}}
                                />
                                <button
                                    type="button"
                                    onClick={() => updateListings(Math.min(MAX_LISTINGS, listings + 1))}
                                    disabled={listings >= MAX_LISTINGS}
                                    className="w-7 h-7 flex items-center justify-center text-[#2766AA] bg-[#EEF5FF] rounded-md hover:bg-[#D9EAFF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-lg font-semibold leading-none"
                                    aria-label="Increase listings"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <img src={starIcon} alt="credits" className="w-4 h-4" />
                            <span className="text-[#2766AA] text-xs font-semibold uppercase tracking-wide">{credits.toLocaleString()} Credits</span>
                        </div>
                    </div>

                    {/* Slider */}
                    <div className="flex flex-col gap-3">
                        <style>{`
                            .listings-slider {
                                -webkit-appearance: none;
                                appearance: none;
                                width: 100%;
                                height: 10px;
                                border-radius: 40px;
                                background: linear-gradient(to right, #78B3F3 ${sliderProgress}%, #E6E4F1 ${sliderProgress}%);
                                outline: none;
                                cursor: pointer;
                            }
                            .listings-slider::-webkit-slider-thumb {
                                -webkit-appearance: none;
                                appearance: none;
                                width: 22px;
                                height: 22px;
                                border-radius: 9999px;
                                background: #78B3F3;
                                cursor: pointer;
                                box-shadow: 0 2px 6px rgba(0,0,0,0.15);
                            }
                            .listings-slider::-moz-range-thumb {
                                width: 22px;
                                height: 22px;
                                border-radius: 9999px;
                                background: #78B3F3;
                                cursor: pointer;
                                border: none;
                                box-shadow: 0 2px 6px rgba(0,0,0,0.15);
                            }
                            .listings-slider::-moz-range-track {
                                height: 10px;
                                border-radius: 40px;
                                background: transparent;
                            }
                            @keyframes headShake {
                                0% { transform: translateX(0); }
                                15% { transform: translateX(-4px); }
                                30% { transform: translateX(4px); }
                                45% { transform: translateX(-3px); }
                                60% { transform: translateX(3px); }
                                75% { transform: translateX(-1px); }
                                100% { transform: translateX(0); }
                            }
                        `}</style>
                        <input
                            type="range"
                            min={MIN_LISTINGS}
                            max={MAX_LISTINGS}
                            step="1"
                            value={listings}
                            onChange={(e) => updateListings(Number(e.target.value))}
                            className="listings-slider"
                            aria-label="Number of listings"
                        />
                        <div className="w-full flex justify-between text-[#898989] text-xs font-semibold">
                            {[MIN_LISTINGS, 100, 200, MAX_LISTINGS].map((tick) => (
                                <button
                                    key={tick}
                                    type="button"
                                    onClick={() => updateListings(tick)}
                                    className={`transition-colors bg-transparent border-none cursor-pointer p-0 font-semibold text-xs ${listings === tick ? 'text-[#2766AA]' : 'text-[#898989] hover:text-[#2766AA]'}`}
                                >
                                    {tick}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Per listing price */}
                    <div className="flex items-baseline gap-2.5 pt-1">
                        <span className="text-[#1F1F1F] text-[24px] font-bold leading-tight">₹{perListing}</span>
                        <span className="text-[#737373] text-base font-medium line-through">₹{MRP}</span>
                        <span className="text-[#737373] text-sm font-medium">/listing</span>
                        <span className="text-[#2E9E5C] text-sm font-bold">{discountPct}% off</span>
                    </div>

                    {/* What you get with these credits */}
                    <div className="flex flex-col gap-2 pt-2 border-t border-[#E8F0FE]">
                        <span className="text-[#505050] text-sm font-semibold font-sans">What you get with {credits.toLocaleString()} credits</span>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { label: 'Listings', count: Math.floor(credits / 50) },
                                { label: 'Listing Score Checks', count: Math.floor(credits / 10) },
                                { label: 'Deal Tag Checks', count: credits },
                                { label: 'Catalogue Health Fixes', count: credits },
                            ].map((item) => (
                                <div key={item.label} className="flex flex-col gap-0.5 px-3 py-2 bg-[#F8FBFF] rounded-lg">
                                    <span className="text-[#2766AA] text-lg font-bold font-sans">Up to {item.count.toLocaleString()}</span>
                                    <span className="text-[#737373] text-xs font-medium font-sans">{item.label}</span>
                                </div>
                            ))}
                        </div>
                        <span className="text-[#898989] text-xs font-medium font-sans">Credits never expire · Actual usage varies based on features used and listing variations</span>
                    </div>
                </div>

                {/* Order Summary Card */}
                <div className="w-[380px] bg-[#FEFEFF] shadow-[4px_46px_134px_14px_rgba(96,146,200,0.10)] rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#D0E7FF] px-5 py-4 flex flex-col gap-3">
                    <h3 className="text-[#505050] text-lg font-semibold font-sans">Order Summary</h3>
                    <div className="w-full flex flex-col gap-3">
                        <SummaryRow label="Credits" value={credits.toLocaleString()} />
                        <SummaryRow label="Per credit" value={`₹${perCredit}`} />
                        <SummaryRow label="Subtotal" value={formatCurrency(mrpTotal)} valueClassName="text-[#505050] text-sm font-semibold font-sans" />
                        <div className="w-full flex justify-between items-center">
                            <span className="text-[#2E9E5C] text-sm font-semibold font-sans">Discount ({discountPct}%)</span>
                            <span className="text-[#2E9E5C] text-sm font-semibold font-sans">-{formatCurrency(savings)}</span>
                        </div>
                        <SummaryRow label="GST (18%)" value={formatCurrency(gst)} />
                        <div className="w-full h-px bg-[#D0E7FF]" />
                        <div className="w-full flex justify-between items-center">
                            <span className="text-[#505050] text-base font-semibold leading-6 font-sans">Total</span>
                            <span className="text-[#505050] text-xl font-bold font-sans">{formatCurrency(total)}</span>
                        </div>
                        <div className="w-full py-2 px-3 bg-[#E8F7ED] rounded-lg flex justify-center items-center">
                            <span className="text-[#2E9E5C] text-sm font-bold font-sans">You save {formatCurrency(savings)} ({discountPct}% off)</span>
                        </div>
                    </div>
                    {/* Billing Address */}
                    <div className="w-full border-t border-[#E8F0FE] pt-3 flex flex-col gap-2">
                        <div className="flex justify-between items-center">
                            <span className="text-[#505050] text-sm font-semibold font-sans">Billing Address</span>
                            {billingForm?.address_line1 && (
                                <button onClick={() => setUseDefault(!useDefault)} className="text-[#2766AA] text-xs font-semibold hover:underline">
                                    {useDefault ? 'Use different address' : 'Use saved address'}
                                </button>
                            )}
                        </div>

                        {useDefault && billingForm?.address_line1 ? (
                            <div className="p-3 bg-[#F8FBFF] rounded-lg border border-[#E8F0FE] flex flex-col gap-1">
                                {billingForm.company_name && <span className="text-[#505050] text-xs font-semibold">{billingForm.company_name}</span>}
                                <span className="text-[#737373] text-xs">{billingForm.address_line1}{billingForm.address_line2 ? `, ${billingForm.address_line2}` : ''}</span>
                                <span className="text-[#737373] text-xs">{billingForm.city}, {billingForm.state} {billingForm.pincode}</span>
                                <span className="text-[#737373] text-xs">{billingForm.country || 'India'}</span>
                                {billingForm.gstin && <span className="text-[#2766AA] text-xs font-medium mt-1">GSTIN: {billingForm.gstin}</span>}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2.5">
                                <div>
                                    <label className="text-[#737373] text-[10px] font-semibold uppercase tracking-wide mb-1 block">Company / Business Name <span className="text-red-400">*</span></label>
                                    <input value={newAddr.company_name} onChange={e => setNewAddr(p => ({ ...p, company_name: e.target.value }))}
                                        placeholder="Enter company or business name" className="w-full px-3 py-2 text-xs rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white" />
                                </div>
                                <div>
                                    <label className="text-[#737373] text-[10px] font-semibold uppercase tracking-wide mb-1 block">GSTIN <span className="text-[#898989] font-normal">(optional)</span></label>
                                    <input value={newAddr.gstin} onChange={e => setNewAddr(p => ({ ...p, gstin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15) }))}
                                        placeholder="e.g. 29ABCDE1234F1Z5" maxLength={15} className="w-full px-3 py-2 text-xs rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white" />
                                </div>
                                <div>
                                    <label className="text-[#737373] text-[10px] font-semibold uppercase tracking-wide mb-1 block">Address Line 1 <span className="text-red-400">*</span></label>
                                    <input value={newAddr.address_line1} onChange={e => setNewAddr(p => ({ ...p, address_line1: e.target.value }))}
                                        placeholder="Street address, P.O. box" className="w-full px-3 py-2 text-xs rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white" />
                                </div>
                                <div>
                                    <label className="text-[#737373] text-[10px] font-semibold uppercase tracking-wide mb-1 block">Address Line 2 <span className="text-[#898989] font-normal">(optional)</span></label>
                                    <input value={newAddr.address_line2} onChange={e => setNewAddr(p => ({ ...p, address_line2: e.target.value }))}
                                        placeholder="Apartment, suite, unit, building, floor" className="w-full px-3 py-2 text-xs rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white" />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-[#737373] text-[10px] font-semibold uppercase tracking-wide mb-1 block">City <span className="text-red-400">*</span></label>
                                        <input value={newAddr.city} onChange={e => setNewAddr(p => ({ ...p, city: e.target.value }))}
                                            placeholder="City" className="w-full px-3 py-2 text-xs rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white" />
                                    </div>
                                    <div>
                                        <label className="text-[#737373] text-[10px] font-semibold uppercase tracking-wide mb-1 block">State <span className="text-red-400">*</span></label>
                                        <input value={newAddr.state} onChange={e => setNewAddr(p => ({ ...p, state: e.target.value }))}
                                            placeholder="State / Province" className="w-full px-3 py-2 text-xs rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-[#737373] text-[10px] font-semibold uppercase tracking-wide mb-1 block">Postal Code <span className="text-red-400">*</span></label>
                                        <input value={newAddr.pincode} onChange={e => setNewAddr(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                                            placeholder="560001" maxLength={6} className="w-full px-3 py-2 text-xs rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white" />
                                    </div>
                                    <div>
                                        <label className="text-[#737373] text-[10px] font-semibold uppercase tracking-wide mb-1 block">Country</label>
                                        <input value={newAddr.country} onChange={e => setNewAddr(p => ({ ...p, country: e.target.value }))}
                                            placeholder="India" className="w-full px-3 py-2 text-xs rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white" />
                                    </div>
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer mt-1">
                                    <input type="checkbox" checked={setAsDefault} onChange={e => setSetAsDefault(e.target.checked)}
                                        className="w-3.5 h-3.5 accent-[#2766AA] rounded" />
                                    <span className="text-[#737373] text-xs font-medium">Save as default billing address</span>
                                </label>
                            </div>
                        )}
                    </div>

                    <button onClick={handlePayment} disabled={paying || !hasBillingAddr} className="w-full h-12 px-5 bg-gradient-to-r from-[#2766AA] to-[#0752B0] rounded-xl flex justify-center items-center cursor-pointer hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">
                        <span className="text-white text-base font-semibold font-sans">{paying ? 'Processing...' : 'Make Payment'}</span>
                    </button>
                    <span className="text-[#737373] text-xs font-medium font-sans">
                        Prices inclusive of 18% GST. One-time charge, no recurring billing.
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CreditSlider;
